import type { ZodType, ZodTypeDef } from "zod"

import { clamp } from "./Number"

export function capitalize<T extends string>(str: T) {
  return (str.charAt(0).toUpperCase() + str.slice(1)) as Capitalize<T>
}

export function pluralize(
  count: number,
  singular: string,
  plural?: string,
): string {
  if (count === 1) return singular
  return plural ?? `${singular}s`
}

export function slugify(input: string) {
  // Replace whitespace with hyphens
  const nowhitespace = input.replace(/ /g, "-")

  // Normalize the string to decompose characters into combining marks and base characters,
  // then remove the combining marks
  const normalized = nowhitespace.normalize("NFD").replace(/\p{M}/g, "")

  // Match and remove any character that is not a letter, digit,
  // underscore or hyphen
  const regex = new RegExp(`[^\\p{L}\\d_.-]`, "gu")
  const slug = normalized.replace(regex, "")

  const escaped = slug.toLowerCase()

  return escaped || ""
}

/**
 * Overflows a string to a strict length
 *
 * @param str - The string to perform actions on
 * @param maxCharacters - Maximum amount of characters to display
 * @returns - A string limited to maxCharacters appended with ...
 */
export function overflow(str: string, maxCharacters: number) {
  if (str.length <= maxCharacters) return str

  return `${str?.slice(0, maxCharacters).trim()}...`
}

export function caseInsensitiveIncludes(str: string, contains: string) {
  return str.toLowerCase().includes(contains.toLowerCase())
}

/**
 * Formats a number of bytes into a human readable format
 * @param bytes - The number of bytes to format
 * @returns - A human readable string of the bytes
 */
export function formatBytes(bytes: number): string {
  if (bytes < 0) return `-${formatBytes(Math.abs(bytes))}`
  if (bytes === 1) return "1 byte"

  const byteUnit = "bytes"
  const units = [byteUnit, "KB", "MB", "GB", "TB"]

  // 1024 is the data unit base
  const index = clamp(Math.floor(Math.log(bytes) / Math.log(1024)), {
    min: 0,
    max: units.length - 1,
  })

  const decimalPlaces = index === 0 ? 0 : 2

  const unit = units[index] ?? byteUnit

  return `${(bytes / Math.pow(1024, index)).toFixed(decimalPlaces)} ${unit}`
}

export function parseJSON<Value>(
  input: string | undefined | null,
  schema: ZodType<Value, ZodTypeDef, unknown>,
): Value | undefined {
  if (!input) return undefined

  try {
    const parsed: unknown = JSON.parse(input)
    const validated = schema.safeParse(parsed)

    if (!validated.success) return undefined

    return validated.data
  } catch {
    return undefined
  }
}

/**
 * Escapes a user-provided string for use in a regular expression
 * Use it to make sure our app doesn't blow up
 * when user puts a character like ")" which we then put into a RegExp.
 *
 * Source: `escape-string-regexp@5.0.0` package
 * https://github.com/sindresorhus/escape-string-regexp/blob/main/index.js
 */
export function escapeStringRegexp(string: string) {
  if (typeof string !== "string") {
    throw new TypeError("Expected a string")
  }

  // Escape characters with special meaning either inside or outside character sets.
  // Use a simple backslash escape when it’s always valid, and a `\xnn` escape when the simpler form would be disallowed by Unicode patterns’ stricter grammar.
  return string.replace(/[|\\{}()[\]^$+*?.]/g, "\\$&").replace(/-/g, "\\x2d")
}
